import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, User } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import Navbar from "@/components/ledger/Navbar";
import ScrollProgress from "@/components/ledger/ScrollProgress";
import Footer from "@/components/ledger/Footer";
import FloatingWhatsApp from "@/components/velora/FloatingWhatsApp";
import { POSTS, buildPostJsonLd } from "./posts.mjs";

const SITE = "https://www.viloraimpex.com";

function ContentBlock({ block }) {
  if (block.type === "p") return <p className="blog-post-p">{block.text}</p>;
  if (block.type === "h2") return <h2 className="blog-post-h2">{block.text}</h2>;
  if (block.type === "h3") return <h3 className="blog-post-h3">{block.text}</h3>;
  if (block.type === "ul")
    return (
      <ul className="blog-post-ul">
        {block.items.map((item, i) => <li key={i}>{item}</li>)}
      </ul>
    );
  if (block.type === "ol")
    return (
      <ol className="blog-post-ol">
        {block.items.map((item, i) => <li key={i}>{item}</li>)}
      </ol>
    );
  if (block.type === "table")
    return (
      <div className="blog-post-table-wrap">
        <table className="blog-post-table">
          <thead>
            <tr>
              {block.headers.map((h, i) => <th key={i}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => <td key={j}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  if (block.type === "callout")
    return <div className={`blog-post-callout blog-post-callout--${block.variant || "brass"}`}>{block.text}</div>;
  if (block.type === "quote")
    return (
      <blockquote className="blog-post-quote">
        <p>{block.text}</p>
        {block.cite ? <cite>— {block.cite}</cite> : null}
      </blockquote>
    );
  if (block.type === "faq")
    return (
      <div className="blog-post-faqs">
        {block.items.map((f, i) => (
          <details key={i} className="blog-post-faq">
            <summary className="blog-post-faq-q">{f.q}</summary>
            <p className="blog-post-faq-a">{f.a}</p>
          </details>
        ))}
      </div>
    );
  return null;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = POSTS[slug];

  useSeo({
    title: post?.seoTitle || "Article — Vilora Impex",
    description: post?.seoDescription,
    path: post ? `/blog/${post.slug}` : "/blog",
    image: post ? `${SITE}${post.heroImage}` : `${SITE}/og.jpg`,
    imageAlt: post?.heroAlt || post?.h1,
    ogType: "article",
    jsonLd: post ? buildPostJsonLd(slug) : null,
  });

  if (!post) {
    return (
      <main className="ledger blog-post-page relative min-h-screen">
        <BlogPostStyles />
        <Navbar />
        <section className="paper" style={{ padding: "80px 24px" }}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <h1 className="display" style={{ fontSize: 40 }}>Post not found</h1>
            <p style={{ marginTop: 12 }}>
              <Link to="/blog" className="story-back-link">Back to the journal</Link>
            </p>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main className="ledger blog-post-page relative min-h-screen" data-testid="blog-post">
      <BlogPostStyles />
      <ScrollProgress />
      <Navbar />

      <article className="blog-post paper">
        <div className="blog-post-shell">
          <nav className="blog-post-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">·</span>
            <Link to="/blog">Journal</Link>
            <span aria-hidden="true">·</span>
            <span aria-current="page">{post.category}</span>
          </nav>

          <div className="blog-post-meta">
            <span className="folio">N&deg; {Object.keys(POSTS).indexOf(slug) + 1 < 10 ? `0${Object.keys(POSTS).indexOf(slug) + 1}` : Object.keys(POSTS).indexOf(slug) + 1}</span>
            <span className="rule rule-grow" />
            <span className="folio" style={{ color: "var(--ink-mute)" }}>
              {post.category.toUpperCase()}
            </span>
          </div>

          <h1 className="display blog-post-title">{post.h1}</h1>

          <p className="blog-post-excerpt">{post.excerpt}</p>

          <div className="blog-post-byline">
            <span className="blog-post-byline-item">
              <User size={13} strokeWidth={1.6} />
              <span>{post.author}</span>
            </span>
            <span className="blog-post-byline-item">
              <Calendar size={13} strokeWidth={1.6} />
              <span>{new Date(post.datePublished).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
            </span>
            <span className="blog-post-byline-item">
              <Clock size={13} strokeWidth={1.6} />
              <span>{post.readingTime} min read</span>
            </span>
          </div>

          {post.heroImage ? (
            <figure className="blog-post-hero">
              <img src={post.heroImage} alt={post.heroAlt || post.h1} width="1280" height="720" loading="eager" />
            </figure>
          ) : null}

          <div className="blog-post-body">
            {post.sections.map((block, i) => <ContentBlock key={i} block={block} />)}
          </div>

          {post.related?.length ? (
            <section className="blog-post-related">
              <h2 className="display blog-post-related-h2">Keep reading</h2>
              <ul>
                {post.related.map((r) => {
                  const rp = POSTS[r];
                  if (!rp) return null;
                  return (
                    <li key={r}>
                      <Link to={`/blog/${rp.slug}`}>
                        <span className="folio">{rp.category}</span>
                        <span className="blog-post-related-title">{rp.h1}</span>
                        <ArrowRight size={14} strokeWidth={1.6} />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          <div className="blog-post-back">
            <Link to="/blog" className="btn-ghost">
              <ArrowLeft size={14} strokeWidth={1.6} />
              <span>Back to the journal</span>
            </Link>
          </div>
        </div>
      </article>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}

function BlogPostStyles() {
  return (
    <style>{`
      .blog-post-page { color: var(--ink); }
      .blog-post-shell { max-width: 780px; margin: 0 auto; padding: clamp(40px, 5vw, 72px) clamp(20px, 4vw, 40px); }
      .blog-post-breadcrumb {
        font-family: 'JetBrains Mono', monospace;
        font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--ink-mute);
        display: flex; gap: 10px; align-items: center; flex-wrap: wrap;
        margin-bottom: 18px;
      }
      .blog-post-breadcrumb a { color: var(--brass-deep); text-decoration: none; }
      .blog-post-meta { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
      .blog-post-title {
        font-size: clamp(34px, 5vw, 56px);
        line-height: 1.05;
        letter-spacing: -0.005em;
        margin: 0 0 16px;
      }
      .blog-post-excerpt {
        font-family: 'Crimson Pro', Georgia, serif;
        font-size: clamp(18px, 1.9vw, 22px);
        line-height: 1.55;
        color: var(--ink-soft);
        max-width: 720px;
        margin: 0 0 22px;
      }
      .blog-post-byline {
        display: flex; flex-wrap: wrap; gap: 14px 24px;
        font-family: 'JetBrains Mono', monospace;
        font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase;
        color: var(--ink-mute); margin-bottom: 28px;
      }
      .blog-post-byline-item { display: inline-flex; align-items: center; gap: 6px; }
      .blog-post-hero { margin: 0 0 36px; }
      .blog-post-hero img {
        width: 100%; height: auto; display: block;
        border: 1px solid rgba(168,132,61,0.28);
        box-shadow: 0 24px 48px -32px rgba(0,0,0,0.4);
      }
      .blog-post-body { font-family: 'Crimson Pro', 'Tiempos Text', Georgia, serif; }
      .blog-post-p {
        font-size: clamp(17px, 1.5vw, 19px);
        line-height: 1.72;
        color: var(--ink-soft);
        margin: 0 0 20px;
      }
      .blog-post-h2 {
        font-family: 'Cormorant Garamond', serif;
        font-weight: 700;
        font-size: clamp(26px, 3vw, 34px);
        line-height: 1.15;
        color: var(--ink);
        margin: 40px 0 16px;
        letter-spacing: -0.003em;
      }
      .blog-post-h3 {
        font-family: 'Cormorant Garamond', serif;
        font-weight: 600;
        font-size: clamp(20px, 2.2vw, 24px);
        color: var(--ink);
        margin: 28px 0 12px;
      }
      .blog-post-ul, .blog-post-ol {
        font-size: clamp(17px, 1.5vw, 19px);
        line-height: 1.7;
        color: var(--ink-soft);
        margin: 0 0 24px;
        padding-left: 24px;
      }
      .blog-post-ul li, .blog-post-ol li { margin-bottom: 8px; }
      .blog-post-table-wrap {
        margin: 24px -8px 28px;
        overflow-x: auto;
        border: 1px solid rgba(168,132,61,0.28);
      }
      .blog-post-table {
        border-collapse: collapse;
        width: 100%;
        font-family: 'JetBrains Mono', monospace;
        font-size: 13px;
      }
      .blog-post-table th, .blog-post-table td {
        padding: 12px 14px;
        text-align: left;
        border-bottom: 1px solid rgba(168,132,61,0.18);
      }
      .blog-post-table th {
        background: rgba(168,132,61,0.08);
        color: var(--brass-deep);
        letter-spacing: 0.10em;
        text-transform: uppercase;
        font-size: 11px;
        font-weight: 600;
      }
      .blog-post-table tr:last-child td { border-bottom: none; }
      .blog-post-callout {
        padding: 16px 20px;
        margin: 24px 0;
        border-left: 3px solid var(--brass);
        background: linear-gradient(90deg, rgba(168,132,61,0.08), transparent 70%);
        font-family: 'Crimson Pro', serif;
        font-style: italic;
        font-size: clamp(16px, 1.4vw, 18px);
        color: var(--ink);
        line-height: 1.6;
      }
      .blog-post-callout--vermilion { border-left-color: var(--vermilion, #C8431F); background: linear-gradient(90deg, rgba(200,67,31,0.08), transparent 70%); }
      .blog-post-quote {
        margin: 28px 0;
        padding: 0 0 0 24px;
        border-left: 2px solid var(--brass);
        font-family: 'Cormorant Garamond', serif;
        font-style: italic;
        font-size: clamp(20px, 2vw, 24px);
        color: var(--ink);
        line-height: 1.4;
      }
      .blog-post-quote cite {
        display: block;
        margin-top: 12px;
        font-family: 'JetBrains Mono', monospace;
        font-style: normal;
        font-size: 12px;
        letter-spacing: 0.14em;
        color: var(--ink-mute);
      }
      .blog-post-faqs { margin: 24px 0; }
      .blog-post-faq {
        border-top: 1px solid rgba(168,132,61,0.24);
        padding: 14px 0;
      }
      .blog-post-faq:last-child { border-bottom: 1px solid rgba(168,132,61,0.24); }
      .blog-post-faq-q {
        font-family: 'Cormorant Garamond', serif;
        font-weight: 600;
        font-size: clamp(17px, 1.6vw, 19px);
        color: var(--ink);
        cursor: pointer;
        list-style: none;
        position: relative;
        padding-right: 28px;
      }
      .blog-post-faq-q::-webkit-details-marker { display: none; }
      .blog-post-faq-q::after {
        content: '+';
        position: absolute;
        right: 4px; top: 0;
        font-family: 'JetBrains Mono', monospace;
        font-size: 18px;
        color: var(--brass);
        transition: transform 200ms ease;
      }
      .blog-post-faq[open] .blog-post-faq-q::after { content: '–'; }
      .blog-post-faq-a {
        margin: 12px 0 0;
        font-family: 'Crimson Pro', serif;
        font-size: 16.5px;
        line-height: 1.65;
        color: var(--ink-soft);
      }
      .blog-post-related {
        margin: 48px 0 32px;
        padding-top: 32px;
        border-top: 1px solid rgba(168,132,61,0.28);
      }
      .blog-post-related-h2 {
        font-size: clamp(24px, 2.6vw, 32px);
        margin: 0 0 18px;
      }
      .blog-post-related ul { list-style: none; padding: 0; margin: 0; display: grid; gap: 8px; }
      .blog-post-related a {
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 16px;
        align-items: center;
        padding: 14px 0;
        border-bottom: 1px dotted rgba(168,132,61,0.32);
        text-decoration: none;
        color: var(--ink);
        transition: color 200ms ease, border-color 200ms ease;
      }
      .blog-post-related a:hover {
        color: var(--brass-deep);
        border-bottom-color: var(--brass);
      }
      .blog-post-related-title {
        font-family: 'Cormorant Garamond', serif;
        font-size: clamp(17px, 1.6vw, 19px);
        font-weight: 500;
      }
      .blog-post-back { margin-top: 36px; }
    `}</style>
  );
}
